// src/utils/axios.js
import axios from "axios";
import { toast } from "react-toastify";

/**
 * Base URL strategy:
 * - Production: VITE_API_BASE_URL=https://cmates.onrender.com/api
 * - Local dev   VITE_API_BASE_URL=http://localhost:5100/api
 *
 * IMPORTANT: since base is /api, every endpoint path below MUST start with /v1/...
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

/** Primary client (has interceptors) */
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  timeout: 15000,
});

/** Bare client (NO interceptors) for refresh to avoid recursion/deadlock */
const refreshClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
  timeout: 15000,
});

/** Attach access token + timezone */
api.interceptors.request.use((config) => {
  const at = localStorage.getItem("cm_at");
  if (at) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${at}`;
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      config.headers = config.headers || {};
      config.headers["X-Timezone"] = tz;
    }
  } catch {
    // Ignore timezone detection failures and continue the request.
  }
  return config;
});

/** Single-flight refresh queue */
let isRefreshing = false;
let queue = [];
const enqueue = () =>
  new Promise((resolve, reject) => queue.push({ resolve, reject }));
const flush = (err, token) => {
  const q = queue.splice(0);
  for (const { resolve, reject } of q) err ? reject(err) : resolve(token);
};

/** Helper: should we skip attempting refresh for this request path? */
function shouldSkipRefresh(path = "") {
  const p = String(path);
  return (
    p.includes("/v1/auth/login") ||
    p.includes("/v1/auth/signup") ||
    p.includes("/v1/auth/verify") || // covers /verify and /verify/request
    p.includes("/v1/auth/password") ||
    p.includes("/v1/auth/refresh")
  );
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error?.config || {};
    const resp = error?.response;
    const status = resp?.status;
    const path = cfg?.url || "";

    // Friendly message construction (unchanged)
    const data = resp?.data;
    const message =
      (Array.isArray(data?.message)
        ? data.message.join(", ")
        : data?.message) ||
      (Array.isArray(data?.errors) ? data.errors.join(", ") : data?.errors) ||
      (!resp ? "Network error. Please check your connection." : null) ||
      "Something went wrong!";

    // === 401 handling with single-flight refresh ===
    if (status === 401 && !cfg._retry && !shouldSkipRefresh(path)) {
      // If another refresh is in-flight, wait for it
      if (isRefreshing) {
        try {
          const token = await enqueue();
          if (token) {
            cfg.headers = {
              ...(cfg.headers || {}),
              Authorization: `Bearer ${token}`,
            };
          }
          return api(cfg);
        } catch (e) {
          return Promise.reject(e);
        }
      }

      // Start a new refresh
      cfg._retry = true;
      isRefreshing = true;
      try {
        // ✅ Use bare client so this call does NOT re-enter this interceptor
        const { data: r } = await refreshClient.post("/v1/auth/refresh", {});
        const newAT = r?.accessToken;
        if (!newAT) throw new Error("No access token from refresh");

        localStorage.setItem("cm_at", newAT);
        flush(null, newAT);

        // Retry original request with new token
        cfg.headers = {
          ...(cfg.headers || {}),
          Authorization: `Bearer ${newAT}`,
        };
        return api(cfg);
      } catch (e) {
        // Wake up waiters with error, clear local token
        flush(e);
        localStorage.removeItem("cm_at");
        // Optional: redirect to login
        // window.location.assign("/login");
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    // Toasts (avoid spamming on 401; those are handled above)
    if (!cfg.skipToast) {
      if (status === 429) toast.error("Too many attempts. Try again later.");
      else if (status && status !== 401) toast.error(message);
      else if (!status) toast.error(message); // network or CORS issues
    }

    return Promise.reject(error);
  },
);

export default api;
